import { useCallback, useEffect, useState } from 'react'
import { NavLink, useOutletContext } from 'react-router-dom'
import { Images, Layers, Plus, RefreshCw, Upload } from 'lucide-react'
import {
  createVisualGroup,
  deleteVisualAsset,
  deleteVisualGroup,
  getVisualAssets,
  getVisualGroups,
  updateVisualAsset,
  updateVisualAssetGroups,
  updateVisualGroup,
  uploadVisualFile,
  type VisualAsset,
  type VisualAssetFilters,
  type VisualGroup
} from '../api/visuals'
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog'
import { useToast } from '../components/ToastProvider'
import { Button, Tabs } from '../components/ui'
import { VisualAssetDialog } from '../features/visuals/VisualAssetDialog'
import { VisualGroupAssetPicker } from '../features/visuals/VisualGroupAssetPicker'
import { VisualGroupDialog } from '../features/visuals/VisualGroupDialog'
import { VisualGroups } from '../features/visuals/VisualGroups'
import { VisualLibrary } from '../features/visuals/VisualLibrary'
import type { AdminOutletContext } from '../layouts/AdminLayout'

type VisualTab = 'library' | 'groups'

type VisualsPageProps = {
  tab: VisualTab
}

const emptyVisualFilters: VisualAssetFilters = {
  group: '',
  search: '',
  status: '',
  type: undefined
}

const createEmptyVisualGroup = (): VisualGroup => ({
  id: '',
  name: '',
  description: '',
  asset_count: 0,
  channel_count: 0,
  created_at: '',
  updated_at: ''
})

export const VisualsPage = ({ tab }: VisualsPageProps) => {
  const { toast } = useToast()
  const { closeMobileActions, setMobileMenuItems } = useOutletContext<AdminOutletContext>()
  const [assets, setAssets] = useState<VisualAsset[]>([])
  const [groups, setGroups] = useState<VisualGroup[]>([])
  const [filters, setFilters] = useState<VisualAssetFilters>(emptyVisualFilters)
  const [uploading, setUploading] = useState<'visual' | 'audio' | null>(null)
  const [selectedAsset, setSelectedAsset] = useState<VisualAsset | null>(null)
  const [selectedGroup, setSelectedGroup] = useState<VisualGroup | null>(null)
  const [membershipGroup, setMembershipGroup] = useState<VisualGroup | null>(null)
  const [assetToDelete, setAssetToDelete] = useState<VisualAsset | null>(null)
  const [groupToDelete, setGroupToDelete] = useState<VisualGroup | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [mobileUploadAction, setMobileUploadAction] = useState<(() => void) | null>(null)
  const [mobileCreateGroupAction, setMobileCreateGroupAction] = useState<(() => void) | null>(null)

  const registerMobileUploadAction = useCallback((action: (() => void) | null): void => {
    setMobileUploadAction(() => action)
  }, [])

  const registerMobileCreateGroupAction = useCallback((action: (() => void) | null): void => {
    setMobileCreateGroupAction(() => action)
  }, [])

  const openCreateGroupDialog = useCallback((): void => {
    setSelectedGroup(createEmptyVisualGroup())
  }, [])

  const loadDashboard = useCallback(async (nextFilters = filters): Promise<void> => {
    const [visualPayload, visualGroupsPayload] = await Promise.all([
      getVisualAssets(nextFilters),
      getVisualGroups()
    ])

    setAssets(visualPayload)
    setGroups(visualGroupsPayload)
  }, [filters])

  useEffect(() => {
    void loadDashboard()
  }, [loadDashboard])

  useEffect(() => {
    setMobileMenuItems(
      <>
        {tab === 'library' && mobileUploadAction && (
          <button
            className="mobile-actions-item"
            onClick={() => {
              closeMobileActions()
              mobileUploadAction()
            }}
            role="menuitem"
            type="button"
          >
            <Upload size={17} />
            <span>Upload visual</span>
          </button>
        )}
        {tab === 'groups' && mobileCreateGroupAction && (
          <button
            className="mobile-actions-item"
            onClick={() => {
              closeMobileActions()
              mobileCreateGroupAction()
            }}
            role="menuitem"
            type="button"
          >
            <Plus size={17} />
            <span>Create group</span>
          </button>
        )}
        <NavLink
          className={tab === 'library' ? 'mobile-actions-item active' : 'mobile-actions-item'}
          end
          onClick={closeMobileActions}
          role="menuitem"
          to="/admin/visuals"
        >
          <Images size={17} />
          <span>Library</span>
        </NavLink>
        <NavLink
          className={tab === 'groups' ? 'mobile-actions-item active' : 'mobile-actions-item'}
          onClick={closeMobileActions}
          role="menuitem"
          to="/admin/visuals/groups"
        >
          <Layers size={17} />
          <span>Groups</span>
        </NavLink>
      </>
    )

    return () => setMobileMenuItems(null)
  }, [closeMobileActions, mobileCreateGroupAction, mobileUploadAction, setMobileMenuItems, tab])

  const applyVisualFilters = async (nextFilters: VisualAssetFilters): Promise<void> => {
    setFilters(nextFilters)
    setAssets(await getVisualAssets(nextFilters))
  }

  const refresh = async (): Promise<void> => {
    await loadDashboard()
    toast('Dashboard refreshed.')
  }

  const confirmDeleteAsset = async (): Promise<void> => {
    if (!assetToDelete) {
      return
    }

    setDeleting(true)
    try {
      await deleteVisualAsset(assetToDelete.id)
      toast(`Deleted visual asset "${assetToDelete.title}".`)
      setAssetToDelete(null)
      await loadDashboard()
    } finally {
      setDeleting(false)
    }
  }

  const confirmDeleteGroup = async (): Promise<void> => {
    if (!groupToDelete) {
      return
    }

    setDeleting(true)
    try {
      await deleteVisualGroup(groupToDelete.id)
      toast(`Deleted visual group "${groupToDelete.name}".`)
      setGroupToDelete(null)
      await loadDashboard()
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <div className="page-header visuals-page-header">
        <div>
          <h1 className="page-title">Manage Visuals</h1>
          <p className="page-subtitle">
            Maintain the flat visual library and assign images or videos to many groups.
          </p>
        </div>
        <div className="page-header-actions">
          <Tabs
            ariaLabel="Visual sections"
            items={[
              { label: 'Library', to: '/admin/visuals' },
              { label: 'Groups', to: '/admin/visuals/groups' }
            ]}
          />
          <Button mobileFullWidth onClick={() => void refresh()}>
            <RefreshCw size={16} /> Refresh
          </Button>
        </div>
      </div>

      {tab === 'library' ? (
        <VisualLibrary
          filters={filters}
          groups={groups}
          items={assets}
          onDelete={async (asset) => setAssetToDelete(asset)}
          onEdit={setSelectedAsset}
          onFilter={applyVisualFilters}
          onRegisterMobileUploadAction={registerMobileUploadAction}
          onUploadComplete={async () => {
            await loadDashboard()
          }}
          onUploadFile={async (file, groupIds, onProgress) => {
            return uploadVisualFile(file, groupIds, onProgress)
          }}
          onUploadStateChange={(isUploading) => setUploading(isUploading ? 'visual' : null)}
          uploading={uploading === 'visual'}
        />
      ) : (
        <VisualGroups
          assets={assets}
          groups={groups}
          onCreate={openCreateGroupDialog}
          onDelete={async (group) => setGroupToDelete(group)}
          onEdit={setSelectedGroup}
          onManageAssets={setMembershipGroup}
          onRegisterMobileCreateAction={registerMobileCreateGroupAction}
        />
      )}

      {selectedAsset && (
        <VisualAssetDialog
          asset={selectedAsset}
          groups={groups}
          onClose={() => setSelectedAsset(null)}
          onSave={async (input) => {
            await updateVisualAsset(input)
            await updateVisualAssetGroups(input.id, input.visualGroupIds)
            toast(`Updated visual asset "${input.title}".`)
            setSelectedAsset(null)
            await loadDashboard()
          }}
        />
      )}

      {selectedGroup && (
        <VisualGroupDialog
          group={selectedGroup}
          onClose={() => setSelectedGroup(null)}
          onSave={async (input) => {
            if (input.id) {
              await updateVisualGroup(input)
              toast(`Updated visual group "${input.name}".`)
            } else {
              await createVisualGroup(input)
              toast(`Created visual group "${input.name}".`)
            }

            setSelectedGroup(null)
            await loadDashboard()
          }}
        />
      )}

      {membershipGroup && (
        <VisualGroupAssetPicker
          assets={assets}
          group={membershipGroup}
          onClose={() => setMembershipGroup(null)}
          onSave={async (assetIds) => {
            await Promise.all(assets.map((asset) => {
              const nextGroupIds = new Set(asset.groups.map((group) => group.id))

              if (assetIds.includes(asset.id)) {
                nextGroupIds.add(membershipGroup.id)
              } else {
                nextGroupIds.delete(membershipGroup.id)
              }

              return updateVisualAssetGroups(asset.id, Array.from(nextGroupIds))
            }))
            toast(`Updated assets in "${membershipGroup.name}".`)
            setMembershipGroup(null)
            await loadDashboard()
          }}
        />
      )}

      {assetToDelete && (
        <DeleteConfirmDialog
          description="This will remove the asset from the visual library and delete its stored image or video files when no other entity uses them. This action cannot be undone."
          isDeleting={deleting}
          itemName={assetToDelete.title}
          onCancel={() => setAssetToDelete(null)}
          onConfirm={confirmDeleteAsset}
          title="Delete Visual Asset"
        />
      )}

      {groupToDelete && (
        <DeleteConfirmDialog
          description="This will delete the visual group and remove its channel and asset bindings. The media assets themselves stay in the library. This action cannot be undone."
          isDeleting={deleting}
          itemName={groupToDelete.name}
          onCancel={() => setGroupToDelete(null)}
          onConfirm={confirmDeleteGroup}
          title="Delete Visual Group"
        />
      )}
    </>
  )
}
